import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import { TextInput } from "@components/Input";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { useEntryIntakeStore } from "@entry";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useErrorBanner, useOnboarding, useSetUserIdentity } from "@hooks";
import { Translations, i18n } from "@i18n";
import { constants } from "@layout";
import { validationSchema } from "./content";
import { NameFormValues } from "./types";

export const NameRoute = () => {
  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<NameFormValues>({
    defaultValues: {
      firstName: useEntryIntakeStore.getState().intake.firstName,
      lastName: useEntryIntakeStore.getState().intake.lastName,
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const [setUserIdentity, { loading }] = useSetUserIdentity();

  const { showErrorBanner } = useErrorBanner();

  const { navigateToNext } = useOnboarding();

  const firstNameController = register("firstName");
  const lastNameController = register("lastName");

  const handleSubmit = async ({ firstName, lastName }: NameFormValues) => {
    try {
      await setUserIdentity({
        variables: {
          firstName,
          lastName,
        },
      });

      navigateToNext();
    } catch {
      showErrorBanner();
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.NameRouteTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.NameRoutePrimaryCTA),
        onClick: handleFormSubmit(handleSubmit),
        loading,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <TextInput
        {...firstNameController}
        label={i18n.t(Translations.NameRouteFirstNameLabel)}
        value={watch("firstName")}
        onChangeText={(text) => setValue("firstName", text, constants.form)}
        error={errors.firstName?.message}
        placeholder={i18n.t(Translations.NameRouteFirstNamePlaceholder)}
      />
      <Spacing size={SpacingSizes.M} />

      <TextInput
        {...lastNameController}
        label={i18n.t(Translations.NameRouteLastNameLabel)}
        value={watch("lastName")}
        onChangeText={(text) => setValue("lastName", text, constants.form)}
        error={errors.lastName?.message}
        placeholder={i18n.t(Translations.NameRouteLastNamePlaceholder)}
      />
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
